import { stateFingerprint } from '@rb/shared/master-data/seed-window-plan';
import type {
  SeedWindowPreview,
  SeedWindowRequest,
} from '@rb/shared/master-data/seed-window.types';
import { resolveVerseEntry } from '@rb/shared/admin-verses';
import type { VerseEntry, VerseFlavor } from '@rb/shared/admin-verses';
import { buildActiveManifestRecord } from '@rb/shared/master-data/active-manifest-builder';
import type { ManifestV2 } from '@rb/shared/master-data/manifest.types';
import { mergeManifestCollections } from '@rb/shared/master-data/merge-manifest';
import { resolveSeedCdnFilename } from '@rb/shared/master-data/seed-flavor';
import type { RawSeedData, SeedResponse } from '@rb/shared/master-data/rpc.types';
import { AdminRPCClient } from '@/client/rpc-client';
import {
  cdnBaseUrl,
  cdnChannel,
  fetchManifest,
  fetchServerManifest,
  isManifestV2,
  resolveVersion,
} from '@/client/cdn-sheets';
import { useActiveVerseStore } from '@/verse/active-verse.store';

/**
 * Seed/sheet helpers for the admin-app Master Data Seeding panel.
 *
 * Mirrors `src/client/seed.client.ts` but routes RPC calls through
 * `AdminRPCClient` (active verse) instead of the game-side `RPCClient`.
 *
 * Local-mode fixture path is intentionally not supported here: admin-app
 * always reads sheets from the public CDN.
 */

/** Server seed keys — the JSON filenames that get seeded to the server */
export const SERVER_SEED_KEYS = [
  'serverWaveShopConfigs',
  'serverLeagueSeasons',
  'serverLeagueTables',
  'serverDegenLeagues',
  'serverValhallas',
  'serverValhallaSeasons',
  'serverDegenRunePool',
  'serverDegenConditionalPool',
  'serverDegenConditionalCountProb',
  'serverDegenSeasons',
  'serverCharacters',
  'serverPets',
  'serverPetSummonConfigs',
  'serverPetSummonLevels',
  'serverQuests',
  'serverQuestMilestones',
  'serverGuideQuests',
  'serverAttendanceRewards',
  'serverEventAttendanceRewards',
  'serverPwaAttendanceRewards',
  'serverStages',
  'serverLeagueRewards',
  'serverEntitlements',
  'serverIdleConfigs',
  'serverPaymentEvents',
  'serverGloryShopItems',
  'serverFreeShopItems',
  'serverGloryGachaEntries',
  'serverDifficulty',
  'serverDifficultyTemplate',
  'serverMetaTags',
  'serverEnemies',
  'serverItems',
  'serverTimedQuests',
  'serverFolkvangrBosses',
  'serverFolkvangrGradeStats',
  'serverFolkvangrRunes',
  'serverFolkvangrSeasons',
  'gameConfig',
] as const;

export type ServerSeedKey = (typeof SERVER_SEED_KEYS)[number];

/**
 * client manifest.json + server-manifest.json 을 받아 병합한 ManifestV2 반환.
 * server 시드 키 버전 해석과 활성 매니페스트 기록(client sheetPins 보존)에 모두 사용.
 * client 가 V2 가 아니면 throw, server 가 V2 가 아니면 client-only 로 폴백.
 */
async function fetchMergedManifest(env: string): Promise<ManifestV2> {
  const [clientManifest, serverManifest] = await Promise.all([
    fetchManifest(env),
    fetchServerManifest(env),
  ]);
  if (!isManifestV2(clientManifest)) {
    throw new Error(
      `[seed] CDN manifest for "${env}" is not v2/v3 (no collections) — cannot resolve versions`
    );
  }
  return isManifestV2(serverManifest)
    ? mergeManifestCollections(clientManifest, serverManifest)
    : clientManifest;
}

export async function seedByKey(key: ServerSeedKey, data: RawSeedData): Promise<SeedResponse> {
  return AdminRPCClient.call('seedByKey', [key, data]);
}

/**
 * 빈 컬렉션 판정 — 시드에서 스킵할지 여부.
 *
 * dev-only 신규 테이블(예: idle_config)이 아직 staging/production 으로 sync_schema
 * 승급 전이면, apply introspection 이 그 테이블을 [] 로 발행한다(빈 컬렉션도 manifest 에
 * 등재). 그 빈 컬렉션을 seedByKey 로 보내면 서버가 던진다 — 구버전 verse 는 "Unknown
 * seed key"(spec 미배포), 신버전 verse 도 "No data for seed key"(items.length===0).
 * 어느 쪽이든 빈 걸 보내는 게 문제이므로, read-side introspection(MR!1326) 의 대칭으로
 * seed-side 에서도 빈 컬렉션을 스킵한다.
 */
export function isEmptySeedPayload(payload: unknown): boolean {
  if (payload == null) return true;
  if (Array.isArray(payload)) return payload.length === 0;
  if (typeof payload === 'object') return Object.keys(payload as object).length === 0;
  return false;
}

export async function fetchSheetData(
  env: string,
  keys: ServerSeedKey[],
  flavor: VerseFlavor,
  pinnedManifest?: ManifestV2
): Promise<RawSeedData> {
  // 매니페스트는 1회만 받고 컬렉션들을 병렬 fetch — fetchCdnCollection(키당 매니페스트 재요청)을 일부러 쓰지 않음.
  // client manifest.json + server-manifest.json 을 병합해 server 시드 키 버전까지 해석한다.
  const manifest = pinnedManifest ?? (await fetchMergedManifest(env));
  const data: RawSeedData = {};
  await Promise.all(
    keys.map(async (key) => {
      const filename = resolveSeedCdnFilename(key, flavor);
      const version = resolveVersion(manifest, filename, key);
      const url = `${cdnBaseUrl()}/${cdnChannel(env)}/${filename}.${version}.json`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Failed to fetch ${url} (${res.status})`);
      (data as Record<string, unknown>)[key] = await res.json();
    })
  );
  return data;
}

/**
 * Run a seeding function against the currently active verse only.
 *
 * Previously this dual-wrote to both web2 and web3 flavors of the requested
 * env, switching the active verse mid-flight. That caused token-dupe (4009)
 * kicks via the SDK's default GameServer instance and was surprising for
 * single-verse seeding flows. The caller picks the verse via the connection
 * UI; we no longer block on registry membership or env match so custom
 * (preview) verses can be seeded too.
 */
async function writeToActiveVerse(
  env: string,
  fn: (verse: VerseEntry) => Promise<SeedResponse>
): Promise<SeedResponse> {
  const store = useActiveVerseStore.getState();
  const activeVerseId = store.activeVerseId;
  if (!activeVerseId) throw new Error('[seed] no active verse — admin must connect first');

  const verse = resolveVerseEntry(activeVerseId);
  console.info(`[seed] writing ${env} data to ${verse.alias} (${verse.verseId})`); // keep
  return fn(verse);
}

// biome-ignore lint/complexity/noStaticOnlyClass: Service class pattern
export class SeedClient {
  /**
   * Seed all SERVER_SEED_KEYS to the currently active verse only.
   *
   * Note: each verse (web2/web3) has independent $global state, so seeding
   * one flavor leaves the other stale. Switch verse and seed again to sync
   * both flavors.
   */
  static async seedFromAssets(env: string): Promise<SeedResponse> {
    return writeToActiveVerse(env, async (verse) => {
      const manifest = await fetchMergedManifest(env);
      const data = await fetchSheetData(env, [...SERVER_SEED_KEYS], verse.flavor, manifest);
      const activeManifest = buildActiveManifestRecord(manifest, {
        schema: env as 'dev' | 'staging' | 'production',
        verse: verse.alias,
        seededAt: new Date().toISOString(),
      });
      const source: SeedWindowRequest = {
        appEnv: verse.alias,
        mode: 'manual',
        sourceVersion: stateFingerprint(manifest),
        data,
        activeManifest,
      };
      const preview = await AdminRPCClient.call<SeedWindowPreview>('adminSeedWindow.preview', [
        source,
      ]);
      return AdminRPCClient.call('adminSeedWindow.apply', [
        {
          source,
          planId: preview.planId,
          baseFingerprint: preview.baseFingerprint,
          previewNow: preview.previewNow,
          operationId: crypto.randomUUID(),
        },
      ]);
    });
  }

  /**
   * Seed a single SERVER_SEED_KEY to the currently active verse only.
   */
  static async seedTargetFromAssets(env: string, target: ServerSeedKey): Promise<SeedResponse> {
    void target;
    return SeedClient.seedFromAssets(env);
  }
}
